import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LeadFormDialog } from './LeadFormDialog';
import { Send, ExternalLink, User } from 'lucide-react';
import {
  ZameenContactHeader,
  ZameenContactActions,
  ZameenField,
  zameenInputClass,
  zameenTextareaClass,
} from './ZameenContactParts';
import { ReportButton } from '@/components/ReportButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { leadApi } from '@/services/api';
import { supabase } from '@/integrations/supabase/client';

interface PropertyContactSidebarProps {
  property: any;
  propertyId: string;
}

export function PropertyContactSidebar({ property, propertyId }: PropertyContactSidebarProps) {
  const { user, profile } = useAuth();
  const refId = (property?.reference_code || `ID${propertyId.slice(0, 8)}`).toUpperCase();
  const defaultMessage = `I would like to inquire about your property EthioRealEstates - ${refId}. Please contact me at your earliest convenience.`;
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    message: defaultMessage,
  });
  const [role, setRole] = useState('buyer');

  useEffect(() => {
    if (!user) return;
    setForm(f => ({
      ...f,
      name: f.name || profile?.full_name || profile?.name || '',
      phone: f.phone || profile?.phone || '',
      email: f.email || user.email || '',
    }));
  }, [user, profile]);
  const [keepInformed, setKeepInformed] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [phoneRevealed, setPhoneRevealed] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);

  // Fetch owner's agent + agency context (publicly readable)
  const [ownerInfo, setOwnerInfo] = useState<any>(null);
  useEffect(() => {
    const ownerId = (property as any)?.user_id;
    if (!ownerId) return;
    (async () => {
      const { data: agent } = await supabase
        .from('agent_profiles')
        .select('photo_url, phone_number, agency_id, agencies(name, logo_url, phone, slug)')
        .eq('user_id', ownerId)
        .maybeSingle();
      if (agent) { setOwnerInfo({ kind: 'agent', ...agent }); return; }
      const { data: dev } = await supabase
        .from('developer_profiles')
        .select('logo_url, phone, company_name, slug')
        .eq('user_id', ownerId)
        .maybeSingle();
      if (dev) setOwnerInfo({ kind: 'developer', ...dev });
    })();
  }, [(property as any)?.user_id]);

  const agency = ownerInfo?.agencies;
  const contactPhone = property.contact_phone || ownerInfo?.phone_number || ownerInfo?.phone || agency?.phone || '+251911000000';
  const agentName = property.contact_person || 'Property Agent';
  const agencyName = agency?.name || ownerInfo?.company_name || property.agency_name || property.developer_name || agentName;
  const displayLogo = agency?.logo_url || ownerInfo?.logo_url || property.agency_logo_url || property.logo_url || null;
  const displayName = agencyName;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await leadApi.create({
        name: form.name,
        phone: form.phone,
        email: form.email || undefined,
        message: form.message || undefined,
        property_id: propertyId,
        source: 'website',
      });
      toast.success('Your inquiry has been sent! The agent will contact you shortly.');
      setForm({
        name: user ? (profile?.full_name || profile?.name || '') : '',
        phone: user ? (profile?.phone || '') : '',
        email: user ? (user.email || '') : '',
        message: defaultMessage,
      });
    } catch (err: any) {
      toast.error(err.message || 'Failed to send inquiry');
    }
    setSubmitting(false);
  };

  const copyNumber = (num: string) => {
    navigator.clipboard.writeText(num);
    toast.success('Number copied to clipboard');
  };

  return (
    <div className="space-y-4">
      {/* Agent Contact Card */}
      <Card className="border-2 border-primary/20">
        <CardContent className="p-5">
          <ZameenContactHeader
            logoUrl={displayLogo}
            agencyName={displayName}
            agentName={agentName}
            refId={refId}
          />

          <ZameenContactActions phone={contactPhone} />

          {/* Contact form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            {!user && (
              <div className="rounded-md border border-primary/20 bg-primary/5 p-2.5 text-xs text-foreground/80">
                Please enter your <strong>name, phone, and email</strong> so the agent can reach you.{' '}
                <Link to="/login" className="text-primary font-medium hover:underline">Log in</Link> to auto-fill.
              </div>
            )}

            <ZameenField label="Name" required htmlFor="sd-name">
              <input
                id="sd-name"
                required
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className={zameenInputClass}
              />
            </ZameenField>

            <ZameenField label="Email" required htmlFor="sd-email">
              <input
                id="sd-email"
                type="email"
                required
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className={zameenInputClass}
              />
            </ZameenField>

            <ZameenField label="Phone" required htmlFor="sd-phone">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground shrink-0">🇪🇹 +251</span>
                <input
                  id="sd-phone"
                  type="tel"
                  required
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  className={zameenInputClass}
                />
              </div>
            </ZameenField>

            <ZameenField label="Message" required htmlFor="sd-message">
              <textarea
                id="sd-message"
                rows={3}
                required
                value={form.message}
                onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                className={zameenTextareaClass}
              />
            </ZameenField>

            {/* Role selector */}
            <div className="space-y-2 pt-1">
              <Label className="text-xs font-medium">I am a:</Label>
              <RadioGroup value={role} onValueChange={setRole} className="flex gap-4">
                {[
                  { value: 'buyer', label: 'Buyer / Tenant' },
                  { value: 'agent', label: 'Agent' },
                  { value: 'other', label: 'Other' },
                ].map((opt) => (
                  <div key={opt.value} className="flex items-center gap-1.5">
                    <RadioGroupItem value={opt.value} id={`sd-role-${opt.value}`} />
                    <Label htmlFor={`sd-role-${opt.value}`} className="text-xs cursor-pointer">{opt.label}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox id="sd-keep-informed" checked={keepInformed} onCheckedChange={(c) => setKeepInformed(!!c)} />
              <Label htmlFor="sd-keep-informed" className="text-xs cursor-pointer">Keep me informed about similar properties.</Label>
            </div>

            <Button type="submit" className="w-full h-11 font-bold gap-2" disabled={submitting}>
              <Send className="h-4 w-4" />
              {submitting ? 'Sending...' : 'Send Message'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Agency / Developer Card */}
      <Card>
        <CardContent className="p-5">
          <div className="text-center">
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
              {agencyName || 'Listed By'}
            </div>
            {displayLogo ? (
              <img src={displayLogo} alt={agencyName || 'Agency'} className="h-16 w-auto mx-auto mb-3 object-contain" />
            ) : (
              <div className="h-16 w-16 mx-auto mb-3 rounded-lg bg-muted flex items-center justify-center">
                <Building2Icon className="h-8 w-8 text-muted-foreground" />
              </div>
            )}
            {property.subscription_tier && (
              <Badge variant="secondary" className="mb-2 text-[10px] uppercase">
                ⭐ {property.subscription_tier}
              </Badge>
            )}
            <div className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground mb-3">
              <User className="h-3.5 w-3.5" />
              <span>{agentName}</span>
            </div>
            <Button variant="outline" size="sm" className="w-full font-semibold text-xs">
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              Agency Profile
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Property */}
      <Card className="bg-muted/50">
        <CardContent className="p-4">
          <ReportButton
            targetType="property"
            targetId={propertyId}
            variant="outline"
            className="w-full border-destructive/30 text-destructive hover:bg-destructive hover:text-destructive-foreground font-semibold text-sm"
            label="Report this property"
          />
        </CardContent>
      </Card>

      <LeadFormDialog
        open={messageOpen}
        onOpenChange={setMessageOpen}
        listingId={propertyId}
        listingLabel="property"
        referenceCode={property?.reference_code}
      />
    </div>
  );
}

function Building2Icon(props: any) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/>
    </svg>
  );
}
